import type { Dispatch, ReactNode, SetStateAction } from 'react';

import { useNavigateToPreset } from '@restDataAccess';
import { useGetPresetByInventoryPath } from '@restDataAccess/camera/cameraControlApi.dataHooks.ts';
import { Badge, Button, FlexAligner, IconButton, Typography } from '@scaleaq/scaleui';
import { CameraInfo, PresetInfo } from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';

import { FeedbackAlert, type FeedbackAlertProps, type FeedbackAlertState } from '@/components';
import { useCameraChanged } from '@/components/cameraStreamWidget/useCameraChanged.ts';
import { Icon, responsive } from '@/utils';

type PresetPositionListProps = {
  alertProps: FeedbackAlertProps;
  camera: CameraInfo;
  children: ReactNode;
  onDelete: (preset: PresetInfo) => void;
  onEdit: (preset: PresetInfo) => void;
  setFeedback: Dispatch<SetStateAction<FeedbackAlertState | undefined>>;
};

/**
 * Live winch state for the preset list. `CameraChanged` updates this component only,
 * so the add-preset form (passed as `children`) does not re-render on every position.
 */
export const PresetPositionList = ({
  alertProps,
  camera,
  children,
  onDelete,
  onEdit,
  setFeedback,
}: PresetPositionListProps) => {
  const { t } = useTranslation(['cameraControl', 'common']);
  const { data: presets, isLoading } = useGetPresetByInventoryPath({
    inventoryPath: camera.path,
  });
  const { mutate: navigateToPreset, isPending: isNavigating } = useNavigateToPreset();
  const liveCamera = useCameraChanged(camera.id);
  const isWinchInMaintenance =
    (liveCamera?.winchInfo ?? camera.winchInfo)?.isInMaintenanceMode ?? false;

  const handleNavigateToPreset = (preset: PresetInfo) => {
    if (!camera.id) {
      setFeedback({
        text: t('preset.navigateMissingCamera', { name: preset.name }),
        variant: 'alarm',
      });
      return;
    }

    navigateToPreset(
      { inventoryId: camera.id, presetId: preset.id },
      {
        onSuccess: () =>
          setFeedback({
            text: t('preset.navigated', { name: preset.name }),
            variant: 'success',
          }),
        onError: error => {
          const message = error instanceof Error ? error.message : '';
          setFeedback({
            text: t('preset.navigateFailed', { name: preset.name, message }),
            variant: 'alarm',
          });
        },
      }
    );
  };

  return (
    <>
      <FeedbackAlert {...alertProps} loading={isLoading || isNavigating} />
      <FlexAligner direction={'column'} gap={responsive.spacing.large}>
        {isWinchInMaintenance && <Badge label={t('winchMaintenanceMode')} type={'warning'} />}
        {children}
        {presets?.map(preset => {
          const presetHasWinchMovement = preset.depth != null || preset.distance != null;

          return (
            <FlexAligner key={preset.id} spaceBetween={true} alignment={'center'}>
              <Typography type={'body_xlarge'}>{preset.name}</Typography>
              <FlexAligner alignment={'center'} fullWidth={false} gap={responsive.spacing.large}>
                <IconButton
                  Icon={Icon.Trash2}
                  size={'medium'}
                  aria-label={t('common:delete')}
                  color={'alarm'}
                  onClick={() => onDelete(preset)}
                />
                <IconButton
                  Icon={Icon.Pencil}
                  size={'medium'}
                  aria-label={t('common:edit')}
                  onClick={() => onEdit(preset)}
                />
                <Button
                  label={t('preset.goTo')}
                  disabled={isNavigating || (isWinchInMaintenance && presetHasWinchMovement)}
                  onClick={() => handleNavigateToPreset(preset)}
                />
              </FlexAligner>
            </FlexAligner>
          );
        })}
      </FlexAligner>
    </>
  );
};
